import type { User } from 'firebase/auth'
import { doc, getDoc, getDocFromServer, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from './firebase/config'
import { updateAuthDisplayName } from './firebase/auth.service'
import type { StudentUser } from '@/types'
import { deriveDisplayName, isBlankName } from '@/utils/displayName'

export type UserRole = 'professor' | 'student' | 'admin'

export interface UserDocument {
  uid: string
  email: string
  role: UserRole
  display_name: string
  photo_url: string | null
  student_id_number: string | null
  email_notifications_enabled: boolean
  is_active: boolean
  email_verified: boolean
  created_at: ReturnType<typeof serverTimestamp>
  created_by: string
  updated_at: ReturnType<typeof serverTimestamp>
  updated_by: string
}

export const userService = {
  /**
   * Crea `users/{uid}`. `display_name` es el **derivado** (`deriveDisplayName`,
   * 2026-11-02) de `requestedName` (lo tecleado en el registro) o, si no se da,
   * del `displayName` de Auth (Google), con el correo de la cuenta.
   *
   * Firestore primero, Auth después: si el `displayName` de Auth está nulo o en
   * blanco, tras escribir el documento se le escribe el mismo derivado. Si eso
   * falla, el documento se queda, se registra en consola y el alta no se rompe
   * (la app móvil derivaría el mismo valor de Auth).
   */
  async createUserDocument(
    user: User,
    role: UserRole = 'student',
    requestedName?: string
  ): Promise<void> {
    const displayName = deriveDisplayName(requestedName ?? user.displayName, user.email)
    try {
      const userDoc: UserDocument = {
        uid: user.uid,
        email: user.email || '',
        role: role,
        display_name: displayName,
        photo_url: user.photoURL || null,
        student_id_number: null,
        email_notifications_enabled: true,
        is_active: true,
        email_verified: user.emailVerified,
        created_at: serverTimestamp(),
        created_by: user.uid,
        updated_at: serverTimestamp(),
        updated_by: user.uid,
      }

      await setDoc(doc(db, 'users', user.uid), userDoc)
    } catch (error) {
      console.error('Error al crear documento de usuario:', error)
      throw error
    }

    if (isBlankName(user.displayName)) {
      try {
        await updateAuthDisplayName(user.uid, displayName)
      } catch (err) {
        console.warn('[nombre] users/{uid} creado, pero el nombre no se sincronizó con Auth:', err)
      }
    }
  },

  // Contra el servidor: decide si se aprovisiona, y una caché vacía se leería
  // como «no existe» (ADR-SEC-013 local — el aprovisionamiento sólo ocurre en un
  // flujo que ya pasó por el consentimiento).
  async userDocumentExists(userId: string): Promise<boolean> {
    const userDoc = await getDocFromServer(doc(db, 'users', userId))
    return userDoc.exists()
  },

  /** Crea `users/{uid}` si no existe. Devuelve `true` si lo creó en esta llamada. */
  async ensureUserDocument(user: User, role: UserRole = 'student'): Promise<boolean> {
    const exists = await this.userDocumentExists(user.uid)

    if (!exists) {
      await this.createUserDocument(user, role)
      return true
    }
    return false
  },

  async getUserDocument(userId: string): Promise<UserDocument | null> {
    const userDoc = await getDoc(doc(db, 'users', userId))

    if (!userDoc.exists()) {
      return null
    }

    return userDoc.data() as UserDocument
  },

  async updateUserProfile(
    userId: string,
    data: { display_name?: string; student_id_number?: string | null; photo_url?: string | null }
  ): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        ...data,
        updated_at: serverTimestamp(),
        updated_by: userId,
      })
    } catch (error) {
      console.error('Error al actualizar perfil:', error)
      throw error
    }
  },

  async updateNotificationPreference(
    userId: string,
    enabled: boolean
  ): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        email_notifications_enabled: enabled,
        updated_at: serverTimestamp(),
        updated_by: userId,
      })
    } catch (error) {
      console.error('Error al actualizar preferencia de notificaciones:', error)
      throw error
    }
  },
}

// Standalone exported functions for use in stores (SRP)
export async function updateUserProfile(
  userId: string,
  data: Partial<StudentUser>
): Promise<void> {
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...data,
      updated_at: serverTimestamp(),
      updated_by: userId,
    })
  } catch (error) {
    console.error('Error al actualizar perfil:', error)
    throw error
  }
}

export async function uploadProfilePhoto(
  userId: string,
  file: File
): Promise<string> {
  try {
    const storageRef = ref(storage, `profile-photos/${userId}/${file.name}`)
    await uploadBytes(storageRef, file)
    const downloadUrl = await getDownloadURL(storageRef)

    // Update the user document with the new photo URL
    await updateDoc(doc(db, 'users', userId), {
      photo_url: downloadUrl,
      updated_at: serverTimestamp(),
      updated_by: userId,
    })

    return downloadUrl
  } catch (error) {
    console.error('Error al subir foto de perfil:', error)
    throw error
  }
}
